import { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { toast, ToastContainer } from "react-toastify";
import "./BookDemoModal.css";

import { HiOutlineChevronLeft, HiOutlineChevronRight } from "react-icons/hi2";

const times = ["9:00 AM", "11:00 AM", "2:00 PM", "4:00 PM"];

export default function BookDemoModal({ show, onClose }) {
  // =========================================================
  // TODAY
  // =========================================================

  const today = new Date();

  // =========================================================
  // CALENDAR STATE
  // =========================================================

  const [currentDate, setCurrentDate] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1),
  );

  const [selectedDate, setSelectedDate] = useState(today);

  const [selectedTime, setSelectedTime] = useState("2:00 PM");

  // =========================================================
  // FORM STATE
  // =========================================================

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);

  // =========================================================
  // LANGUAGE
  // =========================================================

  const getCurrentLanguage = () => {
    const savedLanguage = localStorage.getItem("language");

    if (savedLanguage === "fr") {
      return "fr";
    }

    if (savedLanguage === "pt") {
      return "pt";
    }

    return "en";
  };

  const [currentLanguage, setCurrentLanguage] = useState(getCurrentLanguage);

  // =========================================================
  // LISTEN FOR LANGUAGE CHANGES
  // =========================================================

  useEffect(() => {
    const handleLanguageChange = () => {
      const newLanguage = getCurrentLanguage();

      setCurrentLanguage(newLanguage);
    };

    window.addEventListener("languageChanged", handleLanguageChange);

    return () => {
      window.removeEventListener("languageChanged", handleLanguageChange);
    };
  }, []);

  // =========================================================
  // LOCALE
  // =========================================================

  const localeMap = {
    en: "en-US",
    fr: "fr-FR",
    pt: "pt-PT",
  };

  const locale = localeMap[currentLanguage] || "en-US";

  // =========================================================
  // MONTH NAME
  // =========================================================

  const monthName = new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
  }).format(currentDate);
  // =========================================================
  // DAY NAMES
  // =========================================================

  const dayNames = Array.from({ length: 7 }, (_, index) => {
    const date = new Date(2023, 0, index + 1);

    return new Intl.DateTimeFormat(locale, {
      weekday: "short",
    })
      .format(date)
      .toUpperCase();
  });

  // =========================================================
  // FORM CHANGE
  // =========================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =========================================================
  // CALENDAR
  // =========================================================

  const year = currentDate.getFullYear();

  const month = currentDate.getMonth();

  const firstDay = new Date(year, month, 1).getDay();

  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const days = [];

  // ---------------------------------------------------------
  // PREVIOUS MONTH DAYS
  // ---------------------------------------------------------

  const previousMonthDays = new Date(year, month, 0).getDate();

  for (let i = firstDay - 1; i >= 0; i--) {
    days.push(new Date(year, month - 1, previousMonthDays - i));
  }

  // ---------------------------------------------------------
  // CURRENT MONTH DAYS
  // ---------------------------------------------------------

  for (let i = 1; i <= daysInMonth; i++) {
    days.push(new Date(year, month, i));
  }

  // ---------------------------------------------------------
  // NEXT MONTH DAYS
  // ---------------------------------------------------------

  const remainingDays = days.length % 8;

  if (remainingDays !== 0) {
    const daysToAdd = 8 - remainingDays;

    for (let i = 1; i <= daysToAdd; i++) {
      days.push(new Date(year, month + 1, i));
    }
  }

  // =========================================================
  // SAME DAY
  // =========================================================

  const isSameDay = (d1, d2) =>
    d1.getDate() === d2.getDate() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getFullYear() === d2.getFullYear();

  // =========================================================
  // PAST DATE
  // =========================================================

  const isPastDate = (date) => {
    const currentToday = new Date();

    currentToday.setHours(0, 0, 0, 0);

    const compareDate = new Date(date);

    compareDate.setHours(0, 0, 0, 0);

    return compareDate < currentToday;
  };

 
  // EMAIL REGEX

  const emailRegex =
    /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;

  // PREVIOUS MONTH

  const prevMonth = () => {
    const previous = new Date(year, month - 1, 1);

    const thisMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    if (previous >= thisMonth) {
      setCurrentDate(previous);
    }
  };

  // =========================================================
  // NEXT MONTH
  // =========================================================

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  // =========================================================
  // CURRENT MONTH
  // =========================================================

  const isCurrentMonth =
    year === today.getFullYear() && month === today.getMonth();

  // =========================================================
  // BODY SCROLL
  // =========================================================

  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [show]);

  // =========================================================
  // SUBMIT
  // =========================================================

  const handleSubmit = async () => {
    if (
      !formData.fullName.trim() ||
      !formData.email.trim() ||
      !formData.message.trim() ||
      !selectedDate ||
      !selectedTime
    ) {
      toast.warning("Please fill all the required fields.");
      // alert("Please fill all the required fields.");
      return;
    }

    if (!emailRegex.test(formData.email.trim())) {
      toast.warning("Please enter a valid email address.");
      return;
    }

    if (isPastDate(selectedDate)) {
      toast.warning("Please select a future date.");

      return;
    }

    setLoading(true);

    // =====================================================
    // API LANGUAGE
    // =====================================================

    const languageMap = {
      en: "ENG",
      fr: "FRA",
      pt: "POR",
    };

    const language = languageMap[currentLanguage] || "ENG";

    try {
      const response = await fetch(
        // "https://tnsadmin.tnsglobal.co/api/TnsAPI/SubmitBookYourDemo" UAT
        "https://admin.tnsglobal.co/api/TnsAPI/SubmitBookYourDemo", //Production
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
          },

          body: JSON.stringify({
            fullName: formData.fullName.trim(),

            email: formData.email.trim(),

            date: `${selectedDate.getFullYear()}-${String(
              selectedDate.getMonth() + 1,
            ).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(
              2,
              "0",
            )}`,

            time: selectedTime,

            message: formData.message.trim(),

            language,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Something went wrong");
      }
      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Demo request submitted successfully! Thankyou.",
        confirmButtonText: "OK",
      });
      // alert("Demo request submitted successfully!");

      setFormData({
        fullName: "",
        email: "",
        message: "",
      });

      setSelectedDate(new Date());

      setSelectedTime("2:00 PM");

      onClose();
    } catch (err) {
      console.error(err);

      alert("Failed to submit request.");
    } finally {
      setLoading(false);
    }
  };

  // =========================================================
  // HIDE MODAL
  // =========================================================

  if (!show) {
    return null;
  }

  // =========================================================
  // JSX
  // =========================================================

  return (
    <div className="demo-overlay">
      <div className="demo-modal">
        {/* CLOSE */}

        <button className="demo-close" onClick={onClose}>
          ✕
        </button>

        {/* TITLE */}

        <h2>Book your demo</h2>

        <p>30 minutes with a TNS Global specialist</p>

        <div className="demo-modal-scroll">
          {/* =================================================
              CALENDAR HEADER
          ================================================= */}

          <div className="calendar-header">
            <button
              className="calendar-nav-btn"
              disabled={isCurrentMonth}
              onClick={prevMonth}
            >
              <HiOutlineChevronLeft />
            </button>

            <h4
              key={`${currentLanguage}-${currentDate.getFullYear()}-${currentDate.getMonth()}`}
            >
              {monthName}
            </h4>

            <button className="calendar-nav-btn" onClick={nextMonth}>
              <HiOutlineChevronRight />
            </button>
          </div>
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
          />
          {/* =================================================
              CALENDAR
          ================================================= */}

          <div className="calendar-grid">
            {days.map((day, index) => {
              const isCurrentMonthDay = day.getMonth() === month;

              const past = isPastDate(day);

              return (
                <button
                  key={index}
                  disabled={past || !isCurrentMonthDay}
                  className={`
                      day
                      ${isSameDay(selectedDate, day) ? "active" : ""}
                      ${past ? "disabled" : ""}
                      ${!isCurrentMonthDay ? "other-month" : ""}
                    `}
                  onClick={() => {
                    if (!past && isCurrentMonthDay) {
                      setSelectedDate(day);
                    }
                  }}
                >
                  <small>{dayNames[day.getDay()]}</small>

                  <span>{day.getDate()}</span>
                </button>
              );
            })}
          </div>

          {/* =================================================
              TIME
          ================================================= */}

          <div className="time-grid">
            {times.map((time) => (
              <button
                key={time}
                className={selectedTime === time ? "active" : ""}
                onClick={() => setSelectedTime(time)}
              >
                {time}
              </button>
            ))}
          </div>

          {/* =================================================
              FORM
          ================================================= */}

          <div className="demo-form">
            <input
              name="fullName"
              required
              placeholder="Full Name"
              value={formData.fullName}
              onChange={handleChange}
            />

            <input
              name="email"
              required
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
            />

            <textarea
              name="message"
              required
              rows={4}
              placeholder="Message"
              value={formData.message}
              onChange={handleChange}
            />
          </div>

          {/* =================================================
              BOOK BUTTON
          ================================================= */}

          <div
            className="read-btn-a d-flex book-btn"
            style={{
              justifySelf: "center",
            }}
          >
            <button
              className="read-btn"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Submitting..." : "BOOK A DEMO"}

              {!loading && (
                <img
                  src="/assets/arrow.svg"
                  alt="arrow"
                  className="btn-arrow"
                />
              )}
            </button>
          </div>

          {/* NOTE*/}

          <small className="calendar-note">
            You'll get a calendar invite and call link by email.
          </small>
        </div>
      </div>
    </div>
  );
}
